import { useState } from "react";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import BusinessIcon from "@mui/icons-material/Business";
import { CustomButton } from "src/design/CustomButton";
import { CustomLoadingButton } from "src/design/CustomLoadingButton";
import { SnackBar } from "src/design/SnackBar";
import { Card } from "src/design/Card";
import {
  colorTextPrimary,
  colorTextMuted,
  colorBorder,
  colorSurfaceHover,
  shadowCardHover,
} from "src/design/tokens";
import {
  useOrganizations,
  useCreateOrganization,
} from "src/data/admin/queries/organization";
import { OrganizationDto } from "src/data/admin/types/organization";

interface OrganizationListViewProps {
  onSelect: (org: OrganizationDto) => void;
}

export const OrganizationListView = ({ onSelect }: OrganizationListViewProps) => {
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState("");
  const [snack, setSnack] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  const { data: organizations, isLoading } = useOrganizations();

  const createMutation = useCreateOrganization((org) => {
    setCreateOpen(false);
    setCreateName("");
    setSnack({
      open: true,
      message: `Organization "${org.name}" created successfully.`,
      severity: "success",
    });
  });

  const filtered = (organizations ?? []).filter((org) =>
    org.name.toLowerCase().includes(search.toLowerCase())
  );

  const handleCreate = () => {
    if (!createName.trim()) return;
    createMutation.mutate(
      { name: createName.trim() },
      {
        onError: (err) =>
          setSnack({
            open: true,
            message: err instanceof Error ? err.message : "An error occurred",
            severity: "error",
          }),
      }
    );
  };

  const handleCloseCreate = () => {
    if (createMutation.isPending) return;
    setCreateOpen(false);
    setCreateName("");
  };

  return (
    <>
      <Box>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 3,
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, color: colorTextPrimary }}>
            Organizations
          </Typography>
          <CustomButton
            color="black"
            startIcon={<AddIcon />}
            onClick={() => setCreateOpen(true)}
          >
            New Organization
          </CustomButton>
        </Box>

        {/* Search */}
        <TextField
          size="small"
          placeholder="Search organizations…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: colorTextMuted, fontSize: 18 }} />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 3, width: { xs: "100%", md: 320 } }}
        />

        {/* List */}
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress size={32} />
          </Box>
        ) : filtered.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 8 }}>
            <BusinessIcon sx={{ fontSize: 48, color: colorTextMuted, mb: 1 }} />
            <Typography variant="body2" sx={{ color: colorTextMuted }}>
              {search ? "No organizations match your search." : "No organizations yet."}
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {filtered.map((org) => (
              <Grid item xs={12} md={6} lg={4} key={org.id}>
                <OrganizationCard org={org} onClick={() => onSelect(org)} />
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      {/* Create dialog */}
      <Dialog open={createOpen} onClose={handleCloseCreate} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>New Organization</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            label="Name"
            fullWidth
            size="small"
            value={createName}
            onChange={(e) => setCreateName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleCreate()}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <CustomButton color="inverted" onClick={handleCloseCreate}>
            Cancel
          </CustomButton>
          <CustomLoadingButton
            loading={createMutation.isPending}
            disabled={!createName.trim()}
            onSubmit={handleCreate}
            label="Create"
            color="success"
            fullwidth={false}
          />
        </DialogActions>
      </Dialog>

      <SnackBar
        open={snack.open}
        message={snack.message}
        severity={snack.severity}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      />
    </>
  );
};

interface OrganizationCardProps {
  org: OrganizationDto;
  onClick: () => void;
}

const OrganizationCard = ({ org, onClick }: OrganizationCardProps) => (
  <Card
    sx={{
      flexDirection: "column",
      gap: 1,
      cursor: "pointer",
      transition: "box-shadow 0.15s, background-color 0.15s",
      "&:hover": {
        backgroundColor: colorSurfaceHover,
        boxShadow: shadowCardHover,
      },
    }}
    // Card doesn't forward onClick, wrap in Box
  >
    <Box onClick={onClick} sx={{ width: "100%" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: "50%",
            backgroundColor: colorSurfaceHover,
            border: `1px solid ${colorBorder}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <BusinessIcon sx={{ fontSize: 18, color: colorTextMuted }} />
        </Box>
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 600, color: colorTextPrimary, wordBreak: "break-word" }}
        >
          {org.name}
        </Typography>
      </Box>
      <Box sx={{ display: "flex", gap: 2 }}>
        <Stat label="Restaurants" value={org.restaurantIds.length} />
        <Stat label="Staff" value={org.staffIds.length} />
        <Stat label="Roles" value={org.roleIds.length} />
      </Box>
    </Box>
  </Card>
);

const Stat = ({ label, value }: { label: string; value: number }) => (
  <Box>
    <Typography
      variant="caption"
      sx={{ color: colorTextMuted, display: "block", lineHeight: 1.2 }}
    >
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 600, color: colorTextPrimary }}>
      {value}
    </Typography>
  </Box>
);
